import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, BadgeCheck, Zap, Globe2, Eye } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatPrice, timeAgo } from '../data/constants';
import { LOCALES } from '../lib/i18n';
import { imgUrl } from '../lib/api';

const Badge = ({ icon: Icon, label, variant }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${variant === 'boost' ? 'bg-[#F2B441] text-[#1A1A1A]' : 'bg-white/95 text-[#0F8B5C]'}`}>
    <Icon className="w-2.5 h-2.5" /> {label}
  </span>
);

const ListingCard = ({ listing, compact = false }) => {
  const { t, currency, lang, toggleFav, isFav } = useApp();
  const fav = isFav(listing.id);

  return (
    <Link to={`/listing/${listing.id}`} className="group block" data-testid={`listing-card-${listing.id}`}>
      <div className="relative bg-white rounded-2xl overflow-hidden border border-[#EFEAE0] hover:border-[#0F8B5C]/30 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
        <div className={`relative ${compact ? 'aspect-[4/3]' : 'aspect-square'} overflow-hidden bg-[#FAF7F2]`}>
          <img src={imgUrl(listing.images[0])} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(listing.id); }}
            data-testid={`fav-button-${listing.id}`}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
            aria-label="favorite"
          >
            <Heart className={`w-4 h-4 ${fav ? 'fill-[#E8814A] text-[#E8814A]' : 'text-[#3F3A30]'}`} />
          </button>
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {listing.boosted && <Badge icon={Zap} label={t('feat.boosted')} variant="boost" />}
            {listing.diasporaShipping && <Badge icon={Globe2} label="Diaspora" />}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <span className="text-lg font-bold text-[#1A1A1A] tracking-tight" data-testid={`listing-price-${listing.id}`}>{formatPrice(listing.price, listing.currency, currency, LOCALES[lang])}</span>
            {listing.condition === 'new' && <span className="text-[10px] font-semibold uppercase tracking-wider text-[#0F8B5C]">{t('cond.new')}</span>}
          </div>
          <h3 className="font-medium text-sm text-[#1A1A1A] line-clamp-2 leading-snug group-hover:text-[#0F8B5C] transition-colors mb-2.5">{listing.title}</h3>
          <div className="flex items-center justify-between text-xs text-[#7A7468]">
            <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{listing.location.split(',')[0]}</span></span>
            <span>{timeAgo(listing.postedAt, lang)}</span>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F5F1E8]">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-[#0F8B5C] flex items-center justify-center text-white text-[9px] font-bold">{listing.seller.avatar}</div>
              <span className="text-xs text-[#3F3A30] font-medium truncate max-w-[100px]">{listing.seller.name}</span>
              {listing.seller.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#0F8B5C]" />}
            </div>
            <span className="flex items-center gap-1 text-xs text-[#7A7468]"><Eye className="w-3 h-3" /> {listing.views}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ListingCard;
